import type { CmmdForgeAutomationRun } from "@cmmd-forge/contracts";

import type { ResolvedTaskThreadHistoryGroup } from "~/lib/taskThreadLink";
import { stableTaskAgentName, stableTaskAgentNumber } from "~/lib/taskAgentIdentity";

export type TaskExecutionState = "queued" | "working-now" | "waiting" | "blocked" | "finished";

export interface TaskAgentOwnerPresentation {
  readonly seatId: number;
  readonly stableKey: string;
  readonly displayName: string;
  readonly primaryRole: string;
  readonly capabilities: readonly string[];
  readonly runId: number | null;
  readonly stage: string;
  readonly state: TaskExecutionState;
}

export function deriveTaskAgentOwnerPresentation(
  runs: readonly CmmdForgeAutomationRun[],
  history: readonly ResolvedTaskThreadHistoryGroup[],
): TaskAgentOwnerPresentation | null {
  const threadOwner = deriveThreadOwner(history);
  if (threadOwner !== null) return threadOwner;
  const latest = runs[0];
  if (latest === undefined) return null;
  const owner = readOwner(latest.run.sourceRef["accountableOwner"]);
  if (owner === null) return null;

  return {
    ...owner,
    runId: latest.run.id,
    stage: latest.run.currentStage,
    state: deriveTaskExecutionState(latest, history),
  };
}

function deriveThreadOwner(
  history: readonly ResolvedTaskThreadHistoryGroup[],
): TaskAgentOwnerPresentation | null {
  const master = history[0]?.thread;
  if (!master) return null;
  const thread = master.thread;
  return {
    seatId: stableTaskAgentNumber(thread.id),
    stableKey: `forge-thread:${thread.id}`,
    displayName: stableTaskAgentName(thread.id),
    primaryRole: "task_owner",
    capabilities: master.agentProvider ? [master.agentProvider] : [],
    runId: null,
    stage: deriveThreadOwnerStage(master),
    state: deriveThreadOwnerState(master),
  };
}

function deriveThreadOwnerStage(master: ResolvedTaskThreadHistoryGroup["thread"]): string {
  return master.thread.session?.activeTurnId ? "active_conversation" : "task_conversation";
}

function deriveThreadOwnerState(
  master: ResolvedTaskThreadHistoryGroup["thread"],
): TaskExecutionState {
  if (master.agentStatus === "running") return "working-now";
  if (master.agentStatus === "error") return "blocked";
  if (master.agentStatus === "pending-approval" || master.agentStatus === "pending-input") {
    return "waiting";
  }
  if (master.thread.status === "merged" || master.thread.status === "archived") return "finished";
  return "waiting";
}

function deriveTaskExecutionState(
  entry: CmmdForgeAutomationRun,
  history: readonly ResolvedTaskThreadHistoryGroup[],
): TaskExecutionState {
  if (entry.run.status === "queued") return "queued";
  if (entry.run.status === "blocked" || entry.run.status === "failed") return "blocked";
  if (entry.run.status === "done" || entry.run.status === "cancelled") return "finished";
  if (entry.run.status === "needs_review") return "waiting";

  const agentStatuses = new Set(
    history.flatMap((group) => [
      group.thread.agentStatus,
      ...group.workers.map((worker) => worker.agentStatus),
    ]),
  );
  if (agentStatuses.has("error")) return "blocked";
  return agentStatuses.has("running") ? "working-now" : "waiting";
}

export function temporaryAgentName(threadId: string): string {
  return stableTaskAgentName(threadId);
}

function readOwner(
  value: unknown,
): Omit<TaskAgentOwnerPresentation, "runId" | "stage" | "state"> | null {
  if (!isRecord(value)) return null;
  const seatId = value["id"];
  const stableKey = value["stableKey"];
  const displayName = value["displayName"];
  const primaryRole = value["primaryRole"];
  const capabilities = value["capabilities"];
  if (
    typeof seatId !== "number" ||
    !Number.isInteger(seatId) ||
    typeof stableKey !== "string" ||
    typeof displayName !== "string" ||
    typeof primaryRole !== "string" ||
    !Array.isArray(capabilities) ||
    !capabilities.every((capability) => typeof capability === "string")
  ) {
    return null;
  }
  return {
    seatId,
    stableKey,
    displayName: normalizeAgentFirstName(displayName),
    primaryRole,
    capabilities,
  };
}

function normalizeAgentFirstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] || "Agent";
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
